import BaseComponent from "@js/components/base_component.jsx";
import { getJSON } from "@js/helpers/communication.js";
import HomeStatCard from "./stat_card.jsx";
import HomeChartPanel from "./chart_panel.jsx";
import HomePending from "./pending.jsx";
import {
  INK,
  SERIES,
  DNS_STATE,
  DENSE_ROUNDED_CAP,
  barSeries,
  lineSeries,
  chartBase,
  formatCount,
  formatRate,
} from "./theme";

// The rule across the top of every configuration card, and the color of the
// glyph in its corner. One color for all of them: the cards count different
// things but none of them ranks against the others, and giving one its own
// hue would say that it did.
const CARD_ACCENT = SERIES.accepted;

// The mark in each configuration card's corner, picturing what that card
// counts: mail leaving, a tracked link, mail arriving, and one route fanning
// out across the IP addresses it sends from.
//
// Font Awesome 4 names -- that is the set the Engine UI loads (see
// webpack/src/css/engine_vendor.js), and the icon fonts elsewhere in the
// bundle are not on this page.
const GLYPHS = {
  sending_domains:         "fa-paper-plane",
  shared_tracking_domains: "fa-link",
  receiving_domains:       "fa-inbox",
  routes:                  "fa-sitemap",
};

// The space each chart fills, in pixels. Held here rather than passed at each
// call site so a panel waiting on its figures reserves exactly what the chart
// will occupy.
const EMAILS_SENT_HEIGHT       = 220;
const DELIVERY_ATTEMPTS_HEIGHT = 240;

// The Engine UI home screen on a CloudMTA leader: what is configured across
// the top, and how mail has been moving underneath it.
class EuiHomesShow extends BaseComponent {

  constructor(props) {
    super(props);

    this.state = {
      configuration:      null,
      configurationError: null,
      statistics:         null,
      statisticsError:    null,
    };
  }

  componentDidMount() {
    this._fetch(this.props.configuration_url, "configuration");
    this._fetch(this.props.statistics_url,    "statistics");
  }

  // Fetches one block's figures into the state key that holds them, or the
  // message explaining the failure into `<key>Error`, which is what that block
  // shows in place of its spinner.
  //
  // A block this user may not see has no URL, and nothing is requested for it.
  _fetch(url, key) {
    if (!url) return;

    getJSON(url, (data, error) => {
      this.setState(error ? { [`${key}Error`]: error } : { [key]: data });
    });
  }

  render() {
    return (
      <div>
        {this._renderConfiguration()}
        {this._renderStatistics()}
      </div>
    );
  }

  // ─── Configuration ─────────────────────────────────────────────────────

  _renderConfiguration() {
    // No URL means this user gets no configuration block at all, so there is
    // nothing to wait for either.
    if (!this.props.configuration_url) return null;

    if (this.state.configurationError) return this._renderError(this.state.configurationError);

    const configuration = this.state.configuration;

    // How many cards there will be depends on which features this install has,
    // which is not known until the counts arrive — so the wait is one spinner
    // rather than a row of empty cards.
    if (!configuration) {
      return (
        <div style={{ marginBottom: 20 }}>
          <HomePending height={100} />
        </div>
      );
    }

    const cards = [];

    if (configuration.sending_domains) {
      cards.push(
        this._renderDomainCard({
          key:      "sending_domains",
          title:    I18n.t("labels.sending_domains"),
          glyph:    GLYPHS.sending_domains,
          counts:   configuration.sending_domains,
          indexUrl: Routes.eui_sending_domains_path(),
          filteredUrl: state => Routes.eui_sending_domains_path({ dns_state: state }),
        })
      );
    }

    if (configuration.shared_tracking_domains) {
      cards.push(
        this._renderDomainCard({
          key:      "shared_tracking_domains",
          title:    I18n.t("labels.shared_tracking_domains"),
          glyph:    GLYPHS.shared_tracking_domains,
          counts:   configuration.shared_tracking_domains,
          indexUrl: Routes.eui_shared_tracking_domains_path(),
          filteredUrl: state => Routes.eui_shared_tracking_domains_path({ dns_state: state }),
        })
      );
    }

    if (configuration.receiving_domains) {
      cards.push(
        this._renderDomainCard({
          key:      "receiving_domains",
          title:    I18n.t("labels.receiving_domains"),
          glyph:    GLYPHS.receiving_domains,
          counts:   configuration.receiving_domains,
          indexUrl: Routes.eui_receiving_domains_path(),
          filteredUrl: state => Routes.eui_receiving_domains_path({ dns_state: state }),
        })
      );
    }

    if (configuration.routes) {
      cards.push(
        <HomeStatCard
          key="routes"
          title={I18n.t("labels.routes")}
          href={Routes.eui_routes_path()}
          value={configuration.routes.count}
          accent={CARD_ACCENT}
          glyph={GLYPHS.routes}
          rows={[
            {
              label: I18n.t("home_dashboard.ip_addresses"),
              value: configuration.routes.ip_address_count,
              color: CARD_ACCENT,
            },
          ]}
        />
      );
    }

    if (cards.length === 0) return null;

    // auto-fit keeps every card the same width and lets the row reflow from
    // one across to as many as fit; grid rows equalize the heights, so the
    // cards stay a matched set at every width.
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
          marginBottom: 20,
        }}
      >
        {cards}
      </div>
    );
  }

  // The three DNS states each deep-link to the index page filtered to them,
  // using the same filter values the index's own dropdown posts (see
  // Eui::FiltersByDnsState).
  _renderDomainCard({ key, title, glyph, counts, indexUrl, filteredUrl }) {
    return (
      <HomeStatCard
        key={key}
        title={title}
        href={indexUrl}
        value={counts.total}
        accent={CARD_ACCENT}
        glyph={glyph}
        rows={[
          {
            label: I18n.t("home_dashboard.verified"),
            value: counts.verified,
            color: DNS_STATE.verified,
            href:  filteredUrl("verified_good"),
          },
          {
            label: I18n.t("home_dashboard.regressed"),
            value: counts.regressed,
            color: DNS_STATE.regressed,
            href:  filteredUrl("verified_regressed"),
          },
          {
            label: I18n.t("home_dashboard.not_yet_verified"),
            value: counts.not_verified,
            color: DNS_STATE.not_verified,
            href:  filteredUrl("not_verified"),
          },
        ]}
      />
    );
  }

  // ─── Statistics ────────────────────────────────────────────────────────

  _renderStatistics() {
    if (this.state.statisticsError) return this._renderError(this.state.statisticsError);

    const statistics = this.state.statistics;

    if (statistics && !statistics.available) {
      return (
        <div className="alert alert-info">
          {I18n.t("home_dashboard.statistics_unavailable")}
        </div>
      );
    }

    // Both panels are rendered whether or not their figures have arrived; each
    // stands its own heading and holds its own height until they do.
    return (
      <div style={{ display: "grid", gap: 20 }}>
        {this._renderEmailsSent(statistics && statistics.emails_sent)}
        {this._renderDeliveryAttempts(statistics && statistics.delivery_attempts)}
      </div>
    );
  }

  // Volume per day, as one series. The failure and deferral rates belong to a
  // different scale entirely, so they ride the tooltip and the table rather
  // than a second y-axis.
  _renderEmailsSent(emailsSent) {
    const title    = I18n.t("home_dashboard.emails_sent");
    const subtitle = I18n.t("home_dashboard.last_n_days", { count: this.props.emails_sent_window_days });

    if (!emailsSent) {
      return this._renderPendingPanel(title, subtitle, EMAILS_SENT_HEIGHT);
    }

    const buckets = emailsSent.buckets;

    const tooltip = params => {
      const bucket = buckets[params[0].dataIndex];

      return [
        bucket.tooltip,
        `${I18n.t("home_dashboard.messages")}: <b>${formatCount(bucket.messages)}</b>`,
        `${I18n.t("home_dashboard.failure_rate")}: <b>${formatRate(bucket.failed_rate)}</b>`,
        `${I18n.t("home_dashboard.deferral_rate")}: <b>${formatRate(bucket.deferred_rate)}</b>`,
      ].join("<br/>");
    };

    const option = {
      ...chartBase({
        labels:    buckets.map(bucket => bucket.label),
        tooltip:   tooltip,
        yAxisName: I18n.t("home_dashboard.emails_per_day"),
      }),
      series: [
        barSeries({
          name:    I18n.t("home_dashboard.messages"),
          color:   SERIES.volume,
          data:    buckets.map(bucket => bucket.messages),
          rounded: true,
        }),
      ],
    };

    return (
      <HomeChartPanel
        title={title}
        subtitle={subtitle}
        headline={this._renderTotal(emailsSent.total.messages)}
        option={option}
        height={EMAILS_SENT_HEIGHT}
        tableColumns={[
          I18n.t("home_dashboard.day"),
          I18n.t("home_dashboard.messages"),
          I18n.t("home_dashboard.failure_rate"),
          I18n.t("home_dashboard.deferral_rate"),
        ]}
        tableTotalRow={[
          I18n.t("home_dashboard.total"),
          formatCount(emailsSent.total.messages),
          formatRate(emailsSent.total.failed_rate),
          formatRate(emailsSent.total.deferred_rate),
        ]}
        tableRows={buckets.map(bucket => [
          bucket.tooltip,
          formatCount(bucket.messages),
          formatRate(bucket.failed_rate),
          formatRate(bucket.deferred_rate),
        ])}
      />
    );
  }

  // Attempts per hour, stacked by outcome, with the messages that entered
  // delivery drawn over them as a line.
  _renderDeliveryAttempts(deliveryAttempts) {
    const title    = I18n.t("home_dashboard.delivery_attempts");
    const subtitle = I18n.t("home_dashboard.last_n_days", { count: this.props.delivery_attempts_window_days });

    if (!deliveryAttempts) {
      return this._renderPendingPanel(title, subtitle, DELIVERY_ATTEMPTS_HEIGHT);
    }

    const buckets = deliveryAttempts.buckets;
    const total   = deliveryAttempts.total;

    const outcomes = [
      { key: "accepted", label: I18n.t("home_dashboard.accepted"), color: SERIES.accepted },
      { key: "deferred", label: I18n.t("home_dashboard.deferrals"), color: SERIES.deferred },
      { key: "failed",   label: I18n.t("home_dashboard.failed"),    color: SERIES.failed   },
    ];

    const messages = {
      key:   "messages",
      label: I18n.t("home_dashboard.new_messages"),
      color: SERIES.messages,
      line:  true,
    };

    const tooltip = params => {
      const bucket = buckets[params[0].dataIndex];
      const rows   = params.map(p => `${p.marker}${p.seriesName}: <b>${formatCount(p.value)}</b>`);

      return [ bucket.tooltip, ...rows ].join("<br/>");
    };

    // The last outcome with anything in it is the one that ends the column, so
    // it is the one that gets the rounded cap. -1 for an hour with no traffic
    // at all, which draws nothing to cap.
    const lastOutcomeIn = bucket =>
      outcomes.reduce((last, outcome, index) => (bucket[outcome.key] > 0 ? index : last), -1);

    const option = {
      ...chartBase({
        labels:    buckets.map(bucket => bucket.label),
        tooltip:   tooltip,
        yAxisName: I18n.t("home_dashboard.attempts_per_hour"),
      }),
      series: [
        ...outcomes.map((outcome, index) =>
          barSeries({
            name:       outcome.label,
            color:      outcome.color,
            stack:      "outcomes",
            continuous: true,
            data:       buckets.map(bucket => ({
              value:     bucket[outcome.key],
              itemStyle: lastOutcomeIn(bucket) === index ? DENSE_ROUNDED_CAP : undefined,
            })),
          })
        ),
        lineSeries({
          name:  messages.label,
          color: messages.color,
          data:  buckets.map(bucket => bucket.messages),
        }),
      ],
    };

    return (
      <HomeChartPanel
        title={title}
        subtitle={subtitle}
        headline={this._renderTotal(total.attempts)}
        // The legend carries each series' figure for the window; the chart's
        // own tooltip keeps the bare series names, which is what a per-hour
        // readout wants.
        legend={[ ...outcomes, messages ].map(series => ({
          label:  series.label,
          detail: this._legendDetail(series, total),
          color:  series.color,
          line:   series.line,
        }))}
        option={option}
        height={DELIVERY_ATTEMPTS_HEIGHT}
        tableColumns={[
          I18n.t("home_dashboard.hour"),
          ...[ ...outcomes, messages ].map(series => series.label),
        ]}
        tableTotalRow={[
          I18n.t("home_dashboard.total"),
          ...[ ...outcomes, messages ].map(series => formatCount(total[series.key])),
        ]}
        tableRows={buckets.map(bucket => [
          bucket.tooltip,
          ...[ ...outcomes, messages ].map(series => formatCount(bucket[series.key])),
        ])}
      />
    );
  }

  // A chart panel with everything it can state before its figures arrive: the
  // heading, the window, and the height the chart will fill.
  _renderPendingPanel(title, subtitle, height) {
    return (
      <HomeChartPanel
        title={title}
        subtitle={subtitle}
        headline={this._renderTotal(null)}
        height={height}
        pending={true}
      />
    );
  }

  // A block whose request failed says so once, where its figures would have
  // been, and leaves the rest of the page alone.
  _renderError(message) {
    return <div className="alert alert-danger">{message}</div>;
  }

  _renderTotal(value) {
    return (
      <div style={{ fontSize: 34, fontWeight: 600, color: INK.primary, lineHeight: 1.2 }}>
        {formatCount(value)}
      </div>
    );
  }

  // A series' figure for the whole window, for the legend. Only the outcomes
  // are a share of the attempts — new messages are the quantity those attempts
  // were made at — so only they get a percentage. A window with no attempts at
  // all has no rate either, and shows the count alone rather than an em dash
  // where a percentage should be.
  _legendDetail(series, total) {
    const count = formatCount(total[series.key]);
    const rate  = total[`${ series.key }_rate`];

    return rate == null ? `(${ count })` : `(${ count } - ${ formatRate(rate) })`;
  }

}

export default EuiHomesShow;
